import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { createSelector } from '@reduxjs/toolkit';
import { differenceInMinutes, fromUnixTime, getUnixTime } from 'date-fns';

import type { ResizeCallbackData } from 'react-resizable';
import type { IWeekDay } from '@/types/calendar';
import type { IAvailability, IIntervals, IParsedItem, IUpdateInterval } from '@/components/agent/my-availability/types';
import type { ISetAvailabilityRequest } from '@/api/types';
import type { ScheduleId } from '@/types/schedule';

import { convertMinutesFromMidnightToPX, getHourFromOffsetAndWeekId, translateDayToCalendarIndex } from '@/helpers';
import { useAppDispatch, useAppSelector, usePathParam, useTimezone, useWeek } from '@/hooks';
import { addNewInterval } from '@/store/slices/availabilitySlice';
import { HALF_AN_HOUR_BLOCK_SPAN, HOUR_IN_PX, MINUTES_IN_HOUR } from '@/config/variables';
import { selectAvailabilityList, selectParsedAvailabilityData } from '@/store/selectors/availabilitySelectors';
import { PathParams } from '@/store/enums';
import { selectSelectedSchedulesIds } from '@/store/selectors/scheduleSelectors';

interface IProps {
  findAvailability: (scheduleId: ScheduleId, weekId?: number) => IAvailability | undefined;
  addNewAvailability: (offsetY: number, scheduleId: ScheduleId, day: IWeekDay) => void;
  prepareBasketDataForBackend: (applyToFutureWeeks: boolean) => ISetAvailabilityRequest[];
  getAvailabilityTotalMinutes: (intervals: IIntervals[] | undefined) => number;
  getParsedData: (scheduleId: ScheduleId) => IParsedItem[];
  getParsedDataFromInterval: (options: IUpdateInterval) => IParsedItem | undefined;
  isEditAvailability: boolean;
  toggleEditAvailability: () => void;
  maxConstraintsModifier: (
    handle: ResizeCallbackData['handle'],
    parsedItem: IParsedItem,
    columnIndex: number,
  ) => number | undefined;
}

const useEditAvailabilitySelector = createSelector(
  selectAvailabilityList,
  selectSelectedSchedulesIds,
  selectParsedAvailabilityData,
  (availabilityList, selectedSchedulesIds, parsedAvailabilityData) => ({
    availabilityList,
    selectedSchedulesIds,
    parsedAvailabilityData,
  }),
);

const useEditAvailability = (): IProps => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const { timezoneName } = useTimezone();
  const { selectedWeekId } = useWeek();
  const [weekId] = usePathParam<number>(PathParams.weekId);
  const { availabilityList, selectedSchedulesIds, parsedAvailabilityData } =
    useAppSelector(useEditAvailabilitySelector);

  const getAvailabilityTotalMinutes = useCallback<IProps['getAvailabilityTotalMinutes']>(
    (intervals = []): number => {
      if (!weekId) return 0;
      return intervals.reduce((acc, item) => {
        const minutes = differenceInMinutes(
          fromUnixTime(getHourFromOffsetAndWeekId(weekId, item.endOffset + 1, timezoneName).unix),
          fromUnixTime(getHourFromOffsetAndWeekId(weekId, item.startOffset, timezoneName).unix),
        );
        return acc + minutes;
      }, 0);
    },
    [timezoneName, weekId],
  );

  const findAvailability = useCallback<IProps['findAvailability']>(
    (scheduleId, weekId = selectedWeekId): IAvailability | undefined =>
      availabilityList.find(availability => availability.scheduleId === scheduleId && availability.weekId === weekId),
    [availabilityList, selectedWeekId],
  );

  const isEditAvailability = useMemo<IProps['isEditAvailability']>(() => {
    const query = new URLSearchParams(location.search);
    const queryParam = query.get('editAvailability');

    return queryParam?.toLocaleLowerCase() === 'true';
  }, [location]);

  const toggleEditAvailability = useCallback<IProps['toggleEditAvailability']>(() => {
    const params = new URLSearchParams(history.location.search);
    params.set('editAvailability', String(!isEditAvailability));
    history.push({ search: params.toString() });
  }, [history, isEditAvailability]);

  const getParsedDataFromInterval: IProps['getParsedDataFromInterval'] = options => {
    const parsedItem = parsedAvailabilityData.find(item => item.scheduleId === options.scheduleId)?.parsedData[
      options.intervalIndex
    ];

    if (!parsedItem) return;

    const { resizeData } = parsedItem;
    const isVertical = resizeData.handle === 'n' || resizeData.handle === 's';
    const newParsedItem = { ...parsedItem };

    if (isVertical) {
      const oldStartDate = fromUnixTime(parsedItem.startHour.unix);
      const oldEndDate = fromUnixTime(parsedItem.endHour.unix);

      const direction = resizeData.topOffset === 0 ? 1 : -1;
      const newMinutes = direction * resizeData.resizeOffset * 30;

      if (resizeData.handle === 'n') {
        const newStartDate = new Date(oldStartDate.setMinutes(oldStartDate.getMinutes() + newMinutes));
        const newStartHour = {
          ...newParsedItem.startHour,
          unix: getUnixTime(newStartDate),
          hours: newStartDate.getHours(),
          minutes: newStartDate.getMinutes(),
        };
        Object.assign(newParsedItem, {
          startHour: newStartHour,
          minutesFromMidnight: convertMinutesFromMidnightToPX(newStartDate),
          blockDuration: Math.abs(differenceInMinutes(newStartDate, oldEndDate) / MINUTES_IN_HOUR) * HOUR_IN_PX,
        });
      }

      if (resizeData.handle === 's') {
        const newEndDate = new Date(oldEndDate.setMinutes(oldEndDate.getMinutes() + newMinutes));
        const newEndHour = {
          ...newParsedItem.endHour,
          unix: getUnixTime(newEndDate),
          hours: newEndDate.getHours(),
          minutes: newEndDate.getMinutes(),
        };
        Object.assign(newParsedItem, {
          endHour: newEndHour,
          blockDuration: Math.abs(differenceInMinutes(oldStartDate, newEndDate) / MINUTES_IN_HOUR) * HOUR_IN_PX,
        });
      }
    } else {
      newParsedItem.colSpan = options.intervalToUpdate.colSpan;
      newParsedItem.colOffset = options.intervalToUpdate.colOffset;
    }

    return newParsedItem;
  };

  const addNewAvailability: IProps['addNewAvailability'] = (offsetY, scheduleId, day) => {
    const weekdayIndex = translateDayToCalendarIndex(
      new Date(day.dayYear, day.monthNumber - 1, day.dayNumber).getDay(),
    );

    const hour = Math.trunc(offsetY / HOUR_IN_PX); // hours
    const minutes = offsetY % HOUR_IN_PX > HALF_AN_HOUR_BLOCK_SPAN ? 0.5 : 0.0; // minutes
    const WEEKDAY_COUNTER = weekdayIndex * 48;

    const newInterval = {
      startOffset: (hour + minutes) * 2 + WEEKDAY_COUNTER,
      endOffset: (hour + minutes) * 2 + WEEKDAY_COUNTER,
      colSpan: 1,
      colOffset: 0,
      isNew: true,
      isModified: false,
      toBeDeleted: false,
    };

    const isIntervalAlreadyAdded = parsedAvailabilityData
      .find(item => item.scheduleId === scheduleId)
      ?.intervals.some(item => newInterval.startOffset >= item.startOffset && newInterval.endOffset <= item.endOffset);

    if (!Boolean(isIntervalAlreadyAdded)) {
      dispatch(addNewInterval({ scheduleId, newInterval, timeZone: timezoneName }));
    }
  };

  const prepareBasketDataForBackend: IProps['prepareBasketDataForBackend'] = applyToFutureWeeks => {
    const unmergedIntervals: Record<number, IIntervals[]> = selectedSchedulesIds.reduce(
      (a, v) => ({ ...a, [v]: [] }),
      {},
    );

    return parsedAvailabilityData
      .map((data, columnId, arr) => ({
        scheduleId: data.scheduleId,
        weekId: data.weekId,
        intervals: data.intervals.flatMap(interval => {
          if (interval.colOffset !== 0 || interval.colSpan > 1) {
            for (let x = 0; x < interval.colSpan; x++) {
              unmergedIntervals[arr[columnId + interval.colOffset + x].scheduleId].push({
                ...interval,
                colOffset: 0,
              });
            }
            return [];
          }
          return {
            ...interval,
            colSpan: 1,
          };
        }),
      }))
      .map(data => ({
        ...data,
        intervals: [
          ...unmergedIntervals[data.scheduleId].filter(item => !item.toBeDeleted),
          ...data.intervals
            .filter(item => item.toBeDeleted === false)
            .map(item => ({
              startOffset: item.startOffset,
              endOffset: item.endOffset,
            })),
        ].sort((a, b) => a.startOffset - b.startOffset || a.endOffset - b.endOffset),
      }))
      .map(data => ({ ...data, applyToFutureWeeks }));
  };

  const getParsedData = useCallback<IProps['getParsedData']>(
    scheduleId => parsedAvailabilityData.find(data => data.scheduleId === scheduleId)?.parsedData || [],
    [parsedAvailabilityData],
  );

  const maxConstraintsModifier = useCallback<IProps['maxConstraintsModifier']>(
    (handle, parsedItem, columnIndex) => {
      const _checkCollision = (item1: IParsedItem, item2: IParsedItem) =>
        (item1.startHour.unix >= item2.startHour.unix && item1.startHour.unix < item2.endHour.unix) ||
        (item1.endHour.unix > item2.startHour.unix && item1.endHour.unix <= item2.endHour.unix) ||
        (item1.startHour.unix <= item2.startHour.unix && item1.endHour.unix >= item2.endHour.unix);

      const _findItemVertical = (item: IParsedItem & { columnIndex: number }) => {
        if (columnIndex > item.columnIndex) {
          const isPrevItem =
            columnIndex + parsedItem.colSpan - 1 <= item.columnIndex + item.colSpan - 1 + item.colOffset;
          const isPrevItemStartingAfterParsedItem =
            item.columnIndex + item.colOffset <= columnIndex + parsedItem.colOffset + parsedItem.colSpan - 1;

          // resizing (clicked) parsed item:
          const leftOverlap =
            columnIndex + parsedItem.colOffset <= item.columnIndex + item.colSpan - 1 + item.colOffset;
          const rightOverlap =
            columnIndex + parsedItem.colOffset + parsedItem.colSpan - 1 >= item.columnIndex + item.colOffset;

          return (isPrevItem && isPrevItemStartingAfterParsedItem) || (leftOverlap && rightOverlap);
        }

        if (columnIndex < item.columnIndex) {
          const isNextItem =
            columnIndex + parsedItem.colSpan - 1 >= item.columnIndex + item.colSpan - 1 + item.colOffset;
          const isNextItemStartingBeforeParsedItem =
            item.columnIndex + item.colOffset >= columnIndex + parsedItem.colOffset + parsedItem.colSpan - 1;

          // resizing (clicked) parsed item:
          const leftOverlap = item.columnIndex + item.colOffset <= columnIndex + parsedItem.colSpan - 1;
          const rightOverlap =
            item.columnIndex + item.colOffset + item.colSpan - 1 >= columnIndex + parsedItem.colOffset;

          return (isNextItem && isNextItemStartingBeforeParsedItem) || (leftOverlap && rightOverlap);
        }

        if (columnIndex === item.columnIndex) {
          const isMovedToLeft = item.colOffset + item.colSpan - 1 < parsedItem.colOffset;
          const isMovedToRight = parsedItem.colOffset + parsedItem.colSpan - 1 < item.colOffset;
          return !(isMovedToLeft || isMovedToRight);
        }

        return item.colOffset === 0;
      };

      const len = selectedSchedulesIds.length;
      let val;

      if (handle === 'e') {
        const arr = [];
        for (let x = columnIndex; x < len; x++) {
          const { parsedData } =
            parsedAvailabilityData.find(item => item.scheduleId === selectedSchedulesIds[x + 1]) || {};
          const collisionItem = parsedData
            ?.filter(item => _checkCollision(parsedItem, item))
            .sort((a, b) => a.colOffset - b.colOffset)[0];

          if (collisionItem) {
            arr.push({
              columnIndex: x + 1,
              collisionItem,
            });
          }
        }

        const firstCollisionItem = arr.sort((a, b) => a.collisionItem.colOffset - b.collisionItem.colOffset)[0];

        if (firstCollisionItem) {
          val =
            selectedSchedulesIds.length - firstCollisionItem.columnIndex - firstCollisionItem.collisionItem.colOffset;
        }
      }

      if (handle === 'w') {
        let x = columnIndex;
        while (x--) {
          const { parsedData } = parsedAvailabilityData.find(item => item.scheduleId === selectedSchedulesIds[x]) || {};
          const collisionItem = parsedData?.find(item => _checkCollision(parsedItem, item));
          if (collisionItem) {
            val = x + collisionItem.colOffset + collisionItem.colSpan;
            break;
          }
        }
      }

      if (handle === 's') {
        const nextItem = parsedAvailabilityData
          .map(obj =>
            obj.parsedData?.filter(
              item => item.weekday === parsedItem.weekday && item.startHour.unix >= parsedItem.endHour.unix,
            ),
          )
          .flatMap((obj, index) => obj.map(item => item && { ...item, columnIndex: index }))
          .sort((a, b) => a.startHour.unix - b.startHour.unix || a.columnIndex - b.columnIndex)
          .find(_findItemVertical);

        if (nextItem) {
          const startHour = fromUnixTime(nextItem.startHour.unix);
          const endHour = fromUnixTime(parsedItem.endHour.unix);
          const midnight = new Date(startHour.getTime()).setHours(0, 0, 0, 0);
          val = (differenceInMinutes(startHour, midnight) - differenceInMinutes(endHour, midnight)) / 30;
        }
      }

      if (handle === 'n') {
        const prevItem = parsedAvailabilityData
          .map(obj =>
            obj.parsedData?.filter(
              item => item.weekday === parsedItem.weekday && item.endHour.unix <= parsedItem.startHour.unix,
            ),
          )
          .flatMap((obj, index) => obj.map(item => item && { ...item, columnIndex: index }))
          .sort((a, b) => b.endHour.unix - a.endHour.unix)
          .find(_findItemVertical);

        if (prevItem) {
          const startHour = fromUnixTime(parsedItem.startHour.unix);
          const endHour = fromUnixTime(prevItem.endHour.unix);
          const midnight = new Date(startHour.getTime()).setHours(0, 0, 0, 0);
          val = (differenceInMinutes(startHour, midnight) - differenceInMinutes(endHour, midnight)) / 30;
        }
      }

      return val;
    },
    [parsedAvailabilityData, selectedSchedulesIds],
  );

  return {
    findAvailability,
    addNewAvailability,
    prepareBasketDataForBackend,
    getAvailabilityTotalMinutes,
    getParsedData,
    getParsedDataFromInterval,
    isEditAvailability,
    toggleEditAvailability,
    maxConstraintsModifier,
  };
};

export { useEditAvailability };
